import * as React from 'react';

import { useStateWithDeps } from '@gitbook/util-react-hooks';

import type { PaginationProps } from './Pagination';

export interface UsePaginatedListProps<Item> {
    /** Label for the list of items */
    label?: string;

    /** The list of all items */
    items: Item[];

    /** Number of items per page to render */
    itemsPerPage: number;

    /**
     * Reset pagination when the items change.
     * @default true
     */
    resetWhenItemsChange?: boolean;
}

/**
 * Note: `ListElement` defaults to `HTMLUListElement` but can be overridden by the caller:
 * const paginated = usePaginatedList<ItemType, HTMLOListElement>({...});
 */
export interface UsePaginatedList<Item, ListElement extends HTMLElement = HTMLUListElement> {
    /** DOM reference to attach to the list */
    ref: React.RefObject<ListElement | null>;

    /** Items to render in the current page */
    items: Item[];

    /** Props to pass to the Pagination component to handle pagination. */
    paginationProps: PaginationProps;
}

/**
 * Hook to manage a paginated list of items.
 */
export function usePaginatedList<Item = any, ListElement extends HTMLElement = HTMLUListElement>({
    label,
    items,
    itemsPerPage,
    resetWhenItemsChange = true,
}: UsePaginatedListProps<Item>): UsePaginatedList<Item, ListElement> {
    const ref = React.useRef<ListElement>(null);
    const [page, setPage] = useStateWithDeps(0, resetWhenItemsChange ? [items] : []);
    const mountedRef = React.useRef(false);

    const maxPages = React.useMemo(() => {
        return Math.max(0, Math.ceil(items.length / itemsPerPage) - 1);
    }, [items.length, itemsPerPage]);

    React.useEffect(() => {
        setPage((currentPage) => Math.min(currentPage, maxPages));
    }, [maxPages, setPage]);

    // Detect the scroll parent and scroll to the top when the page changes
    React.useEffect(() => {
        void page; // Explicit usage of page dep.

        if (ref.current && mountedRef.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }

        mountedRef.current = true;
    }, [page]);

    return React.useMemo(() => {
        const totalItems = items.length;
        const pagedData = items.slice(
            page * itemsPerPage,
            Math.min((page + 1) * itemsPerPage, totalItems)
        );
        const hasPrevious = page > 0;
        const hasNext = page < maxPages;

        return {
            ref,
            items: pagedData,
            paginationProps: {
                range: {
                    label,
                    totalItems,
                    itemsPerPage,
                    page,
                },
                previous: hasPrevious ? () => setPage(page - 1) : undefined,
                next: hasNext ? () => setPage(page + 1) : undefined,
            },
        };
    }, [label, items, itemsPerPage, page, maxPages, setPage]);
}
